import { useEffect, useRef, useState } from 'react';
import {
  isEditorFullscreen,
  observeEditorFullscreen,
} from '../util/EditorFullscreen';

export interface UseStickyPositionOptions {
  /** Element that gets pinned to the viewport once scrolled past its natural position. */
  elementRef: React.RefObject<HTMLElement>;
  /**
   * How far below the viewport top the element should sit once fixed. Read
   * fresh on every scroll/resize check (e.g. to sit below another element --
   * like a toolbar -- whose height can itself change). Defaults to pinning
   * flush to the top.
   */
  getStickOffset?: () => number;
  /**
   * Element whose bottom edge caps how far the element can be pinned -- once
   * its bottom scrolls into view, the element stops tracking the viewport
   * and is allowed to scroll away with the rest of the page. Its size is
   * also watched, so content reflow inside it re-triggers the fixed/unfixed
   * decision even without a scroll or window-resize event. Omit for an
   * element with no such limit (e.g. a toolbar that stays fixed for the
   * rest of the page).
   */
  boundaryRef?: React.RefObject<HTMLElement>;
  /** Called after every position check with whether the element is now fixed. */
  onReposition?: (isFixed: boolean) => void;
}

/**
 * Manually pins `elementRef` to the top of the viewport (position: fixed)
 * once the user scrolls past its natural position, mirroring what CSS
 * `position: sticky` would do -- used where an ancestor's overflow/clipping
 * means real `sticky` doesn't take effect.
 */
export function useStickyPosition({
  elementRef,
  getStickOffset = () => 0,
  boundaryRef,
  onReposition,
}: UseStickyPositionOptions) {
  const lastOffsetRef = useRef<number | null>(null);
  const [forcedRenderCount, setForcedRenderCount] = useState(0);

  // Read via a ref so the effect below doesn't need to depend on these
  // (they're commonly passed as fresh inline functions on every render).
  const getStickOffsetRef = useRef(getStickOffset);
  getStickOffsetRef.current = getStickOffset;
  const onRepositionRef = useRef(onReposition);
  onRepositionRef.current = onReposition;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    const getOffsetTop = (el: HTMLElement): number => {
      let offset = el.offsetTop;
      if (el.offsetParent) {
        offset += getOffsetTop(el.offsetParent as HTMLElement);
      }
      return offset;
    };

    // Once fixed, the element's own offsetTop/offsetParent no longer reflect
    // its natural (unfixed) position -- offsetParent becomes null, so
    // offsetTop just reports back whatever inline `top` we set. Don't
    // re-measure (or let that bogus reading clobber the cache) while fixed;
    // trust the last known good value instead.
    const getNaturalOffsetTop = (): number => {
      if (element.style.position === 'fixed') {
        return lastOffsetRef.current ?? 0;
      }
      const currentOffset = getOffsetTop(element);
      if (currentOffset > 0) {
        lastOffsetRef.current = currentOffset;
        return currentOffset;
      }
      return lastOffsetRef.current ?? 0;
    };

    const originalOffsetTop = getNaturalOffsetTop();

    if (originalOffsetTop === 0) {
      // Fullscreen has no natural offset to find, so wait it out rather than
      // retrying on a timer for as long as it stays open.
      if (isEditorFullscreen()) {
        return observeEditorFullscreen(() => {
          if (!isEditorFullscreen()) {
            setForcedRenderCount((n) => n + 1);
          }
        });
      }
      // Not laid out yet (e.g. still hidden while the editor loads).
      // Try again shortly.
      const handle = setTimeout(() => setForcedRenderCount((n) => n + 1), 500);
      return () => clearTimeout(handle);
    }

    const originalPositionStyle = element.style.position;
    const originalTopStyle = element.style.top;

    const unstick = () => {
      element.style.position = originalPositionStyle;
      element.style.top = originalTopStyle;
      onRepositionRef.current?.(false);
    };

    const checkPosition = () => {
      // Fullscreen relocates the element into its own fixed, full-width layout
      // and stops the window scrolling, so a pin there just freezes the
      // pre-fullscreen box.
      if (isEditorFullscreen()) {
        unstick();
        return;
      }

      const naturalOffsetTop = getNaturalOffsetTop();
      const stickOffset = getStickOffsetRef.current();
      const stickPoint = window.scrollY + stickOffset;

      if (stickPoint <= naturalOffsetTop) {
        unstick();
        return;
      }

      let top = stickOffset;
      const boundary = boundaryRef?.current;
      if (boundary) {
        // Once the boundary's bottom comes into view, stop tracking the
        // viewport top so the element scrolls out with the rest of the page.
        const boundaryBottom = getOffsetTop(boundary) + boundary.offsetHeight;
        top = Math.min(
          top,
          boundaryBottom - window.scrollY - element.offsetHeight,
        );
      }

      element.style.position = 'fixed';
      element.style.top = `${top}px`;
      onRepositionRef.current?.(true);
    };

    const boundary = boundaryRef?.current;
    let boundaryObserver: ResizeObserver | null = null;
    if (boundary) {
      boundaryObserver = new ResizeObserver(checkPosition);
      boundaryObserver.observe(boundary);
    }

    const unobserveFullscreen = observeEditorFullscreen(checkPosition);
    window.addEventListener('scroll', checkPosition);
    window.addEventListener('resize', checkPosition);
    checkPosition();

    return () => {
      boundaryObserver?.disconnect();
      unobserveFullscreen();
      window.removeEventListener('scroll', checkPosition);
      window.removeEventListener('resize', checkPosition);
      unstick();
    };
  }, [elementRef, boundaryRef, forcedRenderCount]);
}
